/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function() {
  // ATK currency selector live sync: 2026-06-01.
  var DISPLAY_CURRENCY_KEY = 'atk_display_currency';
  var RATE_CACHE_KEY = 'atk_currency_rates';
  var FALLBACK_CURRENCY_RATES = {
    USD: 1,
    JPY: 157,
    EUR: 0.92,
    KRW: 1375,
    GBP: 0.78,
    CAD: 1.37,
    AUD: 1.52,
    SGD: 1.35,
    HKD: 7.82,
    CNY: 7.24,
    TWD: 32.3,
    THB: 36.6,
    VND: 25450,
    MYR: 4.7,
    IDR: 16200,
    PHP: 58.5,
    INR: 83.5,
    AED: 3.67,
    SAR: 3.75,
    QAR: 3.64,
    KWD: 0.31,
    BHD: 0.38,
    OMR: 0.38,
    CHF: 0.9,
    SEK: 10.6,
    NOK: 10.7,
    DKK: 6.86,
    PLN: 3.95,
    CZK: 23.1,
    HUF: 360,
    RON: 4.58,
    TRY: 32.3,
    ILS: 3.73,
    ZAR: 18.3,
    MXN: 18.2,
    BRL: 5.45,
    NZD: 1.64,
    CLP: 930,
    COP: 4100,
    PEN: 3.75,
    ARS: 900,
    EGP: 48,
    MAD: 10,
    NGN: 1500
  };
  var ZERO_DECIMAL_CURRENCIES = {
    CLP: true,
    HUF: true,
    IDR: true,
    JPY: true,
    KRW: true,
    VND: true
  };
  var PRICE_SELECTOR = [
    '.money',
    '.price',
    '.price-list',
    '.product-item-meta__price-list-container',
    '.product-sticky-form__price',
    '.product-sticky-form__compare-price',
    '.line-item__price-list-container',
    '.mini-cart__line-item-price',
    '.mini-cart__discount-price',
    '.cart__discount-price',
    '.related-popup-price',
    '.change-price-target',
    '.change-old-price-target',
    '.change-discount-target',
    '.pc-product-card__price',
    '.pc-compare-card__price',
    '[data-raw-price]',
    '[data-slot-price]',
    '[data-product-price]'
  ].join(',');
  var CURRENCY_CODE_PATTERN = Object.keys(FALLBACK_CURRENCY_RATES).join('|');
  var MONEY_PATTERN = new RegExp('((?:' + CURRENCY_CODE_PATTERN + ')\\s*)?([$€¥₩₹₫₱£฿₺₪₦₡₲₴₽₸₼])?\\s*([0-9][0-9,.]*)(?:\\s*(' + CURRENCY_CODE_PATTERN + '))?', 'g');
  var rateCache = readRateCache();
  var activeConversion = null;
  var observer = null;
  var renderTimer = null;
  var isRendering = false;

  function optionList(select) {
    return select ? Array.prototype.slice.call(select.options) : [];
  }

  function getSelectedOption(select) {
    return select && select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));

    return match ? decodeURIComponent(match[1]) : '';
  }

  function setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; max-age=31536000; path=/; SameSite=Lax';
  }

  function readRateCache() {
    try {
      return JSON.parse(localStorage.getItem(RATE_CACHE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  }

  function writeRateCache() {
    try {
      localStorage.setItem(RATE_CACHE_KEY, JSON.stringify(rateCache));
    } catch (error) {
      return;
    }
  }

  function getShopifyCurrency() {
    var currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency : {};
    var active = currency.active || window.themeVariables && window.themeVariables.cartCurrency || 'USD';
    var rate = parseFloat(currency.rate || '1');

    return {
      active: active,
      rate: Number.isFinite(rate) && rate > 0 ? rate : 1
    };
  }

  function getSavedCurrency() {
    var savedCurrency = localStorage.getItem(DISPLAY_CURRENCY_KEY) || '';
    var legacyCookie = getCookie('atk_language_currency');

    if (!savedCurrency && legacyCookie) {
      savedCurrency = legacyCookie.split('|')[2] || '';
    }

    return savedCurrency;
  }

  function updateSelectorUi(selector) {
    var languageSelect = selector.querySelector('[data-language-currency-language]');
    var currencySelect = selector.querySelector('[data-language-currency-currency]');
    var currencyOption = getSelectedOption(currencySelect);
    var languageOption = getSelectedOption(languageSelect);
    var flagUrl = currencyOption ? currencyOption.getAttribute('data-flag-url') : '';
    var flag = selector.querySelector('[data-language-currency-select-flag]');
    var triggerFlag = selector.querySelector('[data-language-currency-trigger-flag]');
    var triggerLanguage = selector.querySelector('[data-language-currency-trigger-language]');
    var triggerCurrency = selector.querySelector('[data-language-currency-trigger-currency]');

    if (flag && flagUrl) {
      flag.setAttribute('src', flagUrl);
    }

    if (triggerFlag && flagUrl) {
      triggerFlag.setAttribute('src', flagUrl);
    }

    if (triggerLanguage && languageOption) {
      triggerLanguage.textContent = languageOption.textContent.trim();
    }

    if (triggerCurrency && currencyOption) {
      triggerCurrency.textContent = currencyOption.getAttribute('data-currency-code') || currencyOption.value;
    }
  }

  function syncCurrencySelections(currencyCode) {
    document.querySelectorAll('[data-language-currency-currency]').forEach(function(select) {
      if (currencyCode && optionList(select).some(function(option) { return option.value === currencyCode; })) {
        select.value = currencyCode;
      }

      var selector = select.closest('[data-language-currency-selector]');

      if (selector) {
        updateSelectorUi(selector);
      }
    });
  }

  function parseAmount(value) {
    var normalized = value.replace(/[^\d,.]/g, '');
    var hasComma = normalized.indexOf(',') !== -1;
    var hasDot = normalized.indexOf('.') !== -1;

    if (hasComma && hasDot) {
      normalized = normalized.replace(/,/g, '');
    } else if (hasComma && !hasDot && /,\d{1,2}$/.test(normalized)) {
      normalized = normalized.replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }

    return parseFloat(normalized);
  }

  function formatAmount(amount, currencyCode) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || navigator.language || 'en', {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: ZERO_DECIMAL_CURRENCIES[currencyCode] ? 0 : 2
      }).format(amount);
    } catch (error) {
      return currencyCode + ' ' + amount.toFixed(ZERO_DECIMAL_CURRENCIES[currencyCode] ? 0 : 2);
    }
  }

  function convertText(text, conversion) {
    return text.replace(MONEY_PATTERN, function(match, leadingCode, symbol, amountText, trailingCode) {
      var amount = parseAmount(amountText);

      if (!Number.isFinite(amount)) {
        return match;
      }

      if (!leadingCode && !symbol && !trailingCode && !/[.,]\d{2}$/.test(amountText)) {
        return match;
      }

      return formatAmount(amount * conversion.targetRate / conversion.activeRate, conversion.targetCurrency);
    });
  }

  function getTextNodes(element) {
    var nodes = [];
    var walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode: function(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) {
          return NodeFilter.FILTER_REJECT;
        }

        if (node.parentElement && node.parentElement.closest('script, style, [data-atk-currency-skip]')) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var node = walker.nextNode();

    while (node) {
      nodes.push(node);
      node = walker.nextNode();
    }

    return nodes;
  }

  function renderPrices() {
    if (!activeConversion) {
      return;
    }

    isRendering = true;

    document.querySelectorAll(PRICE_SELECTOR).forEach(function(element) {
      getTextNodes(element).forEach(function(node) {
        if (!node.__atkOriginalPriceText || node.nodeValue !== node.__atkLastRenderedPriceText) {
          node.__atkOriginalPriceText = node.nodeValue;
        }

        var nextText = activeConversion.targetCurrency === activeConversion.activeCurrency
          ? node.__atkOriginalPriceText
          : convertText(node.__atkOriginalPriceText, activeConversion);

        node.nodeValue = nextText;
        node.__atkLastRenderedPriceText = nextText;
      });
    });

    isRendering = false;
  }

  function scheduleRender() {
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(renderPrices, 80);
  }

  function watchPrices() {
    if (observer || !document.body) {
      return;
    }

    observer = new MutationObserver(function() {
      if (!isRendering && activeConversion) {
        scheduleRender();
      }
    });

    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
  }

  function parseRateFromHtml(html, currencyCode) {
    var match = html.match(/Shopify\.currency\s*=\s*({.*?});/);

    if (!match) {
      return 0;
    }

    try {
      var currency = JSON.parse(match[1]);
      var rate = parseFloat(currency.rate || '0');

      return currency.active === currencyCode && Number.isFinite(rate) && rate > 0 ? rate : 0;
    } catch (error) {
      return 0;
    }
  }

  function getRate(currencyOption, activeCurrency) {
    var currencyCode = currencyOption ? currencyOption.getAttribute('data-currency-code') : '';
    var rateUrl = currencyOption ? currencyOption.getAttribute('data-rate-url') : '';
    var fallbackRate = FALLBACK_CURRENCY_RATES[currencyCode] || 0;

    if (!currencyCode) {
      return Promise.reject();
    }

    if (currencyCode === activeCurrency) {
      return Promise.resolve(getShopifyCurrency().rate);
    }

    if (fallbackRate && (!rateUrl || currencyOption.getAttribute('data-fallback-rate') === 'true')) {
      rateCache[currencyCode] = { rate: fallbackRate, time: Date.now() };
      writeRateCache();
      return Promise.resolve(fallbackRate);
    }

    if (rateCache[currencyCode] && Date.now() - rateCache[currencyCode].time < 86400000) {
      return Promise.resolve(rateCache[currencyCode].rate);
    }

    return fetch(rateUrl || '/', { credentials: 'omit' }).then(function(response) {
      return response.text();
    }).then(function(html) {
      var rate = parseRateFromHtml(html, currencyCode);

      if (!rate) {
        if (fallbackRate) {
          rateCache[currencyCode] = { rate: fallbackRate, time: Date.now() };
          writeRateCache();

          return fallbackRate;
        }

        throw new Error('Missing currency rate');
      }

      rateCache[currencyCode] = { rate: rate, time: Date.now() };
      writeRateCache();

      return rate;
    }).catch(function(error) {
      if (fallbackRate) {
        rateCache[currencyCode] = { rate: fallbackRate, time: Date.now() };
        writeRateCache();

        return fallbackRate;
      }

      throw error;
    });
  }

  function applyDisplayCurrency(currencySelect) {
    var currencyOption = getSelectedOption(currencySelect);
    var currencyCode = currencyOption ? currencyOption.getAttribute('data-currency-code') : '';
    var shopifyCurrency = getShopifyCurrency();

    if (!currencyOption || !currencyCode) {
      return;
    }

    syncCurrencySelections(currencyCode);

    getRate(currencyOption, shopifyCurrency.active).then(function(targetRate) {
      activeConversion = {
        activeCurrency: shopifyCurrency.active,
        activeRate: shopifyCurrency.rate,
        targetCurrency: currencyCode,
        targetRate: targetRate
      };

      renderPrices();
      watchPrices();
    }).catch(function() {
      activeConversion = null;
    });
  }

  function initLocalizationSelector(selector) {
    var currencySelect = selector.querySelector('[data-language-currency-currency]');
    var savedCurrency = getSavedCurrency();

    if (currencySelect && savedCurrency && optionList(currencySelect).some(function(option) { return option.value === savedCurrency; })) {
      currencySelect.value = savedCurrency;
    }

    updateSelectorUi(selector);

    if (currencySelect) {
      applyDisplayCurrency(currencySelect);
    }
  }

  document.querySelectorAll('[data-language-currency-selector]').forEach(initLocalizationSelector);

  document.addEventListener('change', function(event) {
    if (event.target.matches('[data-language-currency-currency]')) {
      var currencyCode = event.target.value;

      localStorage.setItem(DISPLAY_CURRENCY_KEY, currencyCode);
      setCookie(DISPLAY_CURRENCY_KEY, currencyCode);
      applyDisplayCurrency(event.target);
    }

    if (event.target.matches('[data-language-currency-language]')) {
      var selector = event.target.closest('[data-language-currency-selector]');

      if (selector) {
        updateSelectorUi(selector);
      }
    }
  });

  document.addEventListener('submit', function(event) {
    var form = event.target;
    var selector = form.querySelector('[data-language-currency-selector]');

    if (!selector) {
      return;
    }

    var languageSelect = selector.querySelector('[data-language-currency-language]');
    var currencySelect = selector.querySelector('[data-language-currency-currency]');
    var countryInput = selector.querySelector('[data-language-currency-country-code]');
    var languageOption = getSelectedOption(languageSelect);
    var currencyOption = getSelectedOption(currencySelect);
    var languageCode = languageSelect ? languageSelect.value : '';
    var countryCode = languageOption ? languageOption.getAttribute('data-country-code') : selector.getAttribute('data-current-country');
    var currencyCode = currencyOption ? currencyOption.getAttribute('data-currency-code') : '';

    if (!countryInput || !countryCode || !languageCode) {
      return;
    }

    countryInput.value = countryCode;

    if (currencyCode) {
      localStorage.setItem(DISPLAY_CURRENCY_KEY, currencyCode);
      setCookie(DISPLAY_CURRENCY_KEY, currencyCode);
    }

    document.cookie = 'atk_language_currency=' + encodeURIComponent(languageCode + '|' + countryCode + '|' + currencyCode) + '; max-age=31536000; path=/; SameSite=Lax';
  });
})();

(function() {
  function setFooterSubmenuState(toggle, submenu, isOpen) {
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    submenu.hidden = !isOpen;
    submenu.toggleAttribute('open', isOpen);
    submenu.style.overflow = isOpen ? 'visible' : '';

    try {
      submenu.open = isOpen;
    } catch (error) {
      return;
    }
  }

  function initFooterSubmenus(root) {
    (root || document).querySelectorAll('.shopify-section--footer .footer__nested-toggle[aria-controls]').forEach(function(toggle) {
      var submenu = document.getElementById(toggle.getAttribute('aria-controls'));

      if (!submenu) {
        return;
      }

      setFooterSubmenuState(toggle, submenu, false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initFooterSubmenus(document);
    });
  } else {
    initFooterSubmenus(document);
  }

  document.addEventListener('shopify:section:load', function(event) {
    initFooterSubmenus(event.target);
  });

  document.addEventListener('click', function(event) {
    var toggle = event.target.closest('.shopify-section--footer .footer__nested-toggle[aria-controls]');

    if (!toggle) {
      return;
    }

    var submenu = document.getElementById(toggle.getAttribute('aria-controls'));

    if (!submenu) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    setFooterSubmenuState(toggle, submenu, toggle.getAttribute('aria-expanded') !== 'true');
  }, true);
})();
